/**
 * Build & trigger download of an RFC 5545 .ics file for an event ad.
 * All-day events use the DATE value type; timed events use DATE-TIME in UTC.
 */
function pad(n) { return String(n).padStart(2, '0'); }

function toICSDate(iso) {
	// "YYYY-MM-DD" -> "YYYYMMDD"
	return iso.replace(/-/g, '');
}

function toICSDateTimeUTC(iso, time) {
	// iso "YYYY-MM-DD", time "HH:MM" -> "YYYYMMDDTHHMMSSZ" in UTC treating local as UTC
	const [y, mo, d] = iso.split('-').map(Number);
	const [h, mi] = (time || '00:00').split(':').map(Number);
	const dt = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
	return `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;
}

function icsEscape(s = '') {
	return String(s).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

function foldLine(line) {
	// RFC 5545: lines SHOULD NOT be longer than 75 octets; fold at 73 with CRLF+space.
	if (line.length <= 73) return line;
	const chunks = [];
	for (let i = 0; i < line.length; i += 73) chunks.push((i === 0 ? '' : ' ') + line.slice(i, i + 73));
	return chunks.join('\r\n');
}

/**
 * @param {Object} event — {id, title, description, start_date, end_date, start_time, location_name, registration_url, organizer_name}
 * @param {string} siteOrigin — e.g. window.location.origin for the URL field
 */
export function buildICS(event, siteOrigin = '') {
	const nowStamp = new Date();
	const dtstamp = `${nowStamp.getUTCFullYear()}${pad(nowStamp.getUTCMonth() + 1)}${pad(nowStamp.getUTCDate())}T${pad(nowStamp.getUTCHours())}${pad(nowStamp.getUTCMinutes())}${pad(nowStamp.getUTCSeconds())}Z`;
	const uid = `event-${event.id}@equimarket`;
	const url = event.slug ? `${siteOrigin}/events/${event.slug}` : `${siteOrigin}/events/${event.id}`;

	const lines = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//EquiMarket//Events//EN',
		'CALSCALE:GREGORIAN',
		'METHOD:PUBLISH',
		'BEGIN:VEVENT',
		`UID:${uid}`,
		`DTSTAMP:${dtstamp}`,
		`SUMMARY:${icsEscape(event.title || '')}`,
	];

	if (event.start_time) {
		// Timed event
		const dtstart = toICSDateTimeUTC(event.start_date, event.start_time);
		let dtend;
		if (event.end_date && event.end_date !== event.start_date) {
			// Multi-day timed event: use same start_time on end_date
			dtend = toICSDateTimeUTC(event.end_date, event.start_time);
		} else {
			// Single-day timed event: default to 2 hours after start
			const [y, mo, d] = event.start_date.split('-').map(Number);
			const [h, mi] = event.start_time.split(':').map(Number);
			const endDate = new Date(Date.UTC(y, mo - 1, d, h + 2, mi, 0));
			dtend = `${endDate.getUTCFullYear()}${pad(endDate.getUTCMonth() + 1)}${pad(endDate.getUTCDate())}T${pad(endDate.getUTCHours())}${pad(endDate.getUTCMinutes())}00Z`;
		}
		lines.push(`DTSTART:${dtstart}`);
		lines.push(`DTEND:${dtend}`);
	} else {
		// All-day. RFC 5545: DTEND for all-day is exclusive (day after the last day)
		const startIso = event.start_date;
		const endIso = event.end_date || event.start_date;
		// Add one day to endIso to make DTEND exclusive
		const [y, mo, d] = endIso.split('-').map(Number);
		const endExclusive = new Date(Date.UTC(y, mo - 1, d));
		endExclusive.setUTCDate(endExclusive.getUTCDate() + 1);
		const dtendIso = `${endExclusive.getUTCFullYear()}-${pad(endExclusive.getUTCMonth() + 1)}-${pad(endExclusive.getUTCDate())}`;
		lines.push(`DTSTART;VALUE=DATE:${toICSDate(startIso)}`);
		lines.push(`DTEND;VALUE=DATE:${toICSDate(dtendIso)}`);
	}

	const descParts = [];
	if (event.description) descParts.push(event.description);
	if (event.registration_url) descParts.push(`Registration: ${event.registration_url}`);
	if (url) descParts.push(`Details: ${url}`);
	if (descParts.length) lines.push(`DESCRIPTION:${icsEscape(descParts.join('\n\n'))}`);
	if (event.location_name) lines.push(`LOCATION:${icsEscape(event.location_name)}`);
	if (event.organizer_name) lines.push(`ORGANIZER;CN=${icsEscape(event.organizer_name)}:MAILTO:noreply@equimarket.pt`);
	if (url) lines.push(`URL:${url}`);

	lines.push('END:VEVENT');
	lines.push('END:VCALENDAR');

	return lines.map(foldLine).join('\r\n');
}

export function downloadICS(event, siteOrigin = '') {
	const ics = buildICS(event, siteOrigin);
	const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	const safeName = (event.title || 'event').replace(/[^a-z0-9-_]+/gi, '-').slice(0, 60);
	a.download = `${safeName}.ics`;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}